import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { PulseActivityLogEntry, PulseConnectionResponse } from '@orion/contracts';
import { Button, TagInput } from '@orion/ui';
import { ApiError } from '../../../lib/api-client.js';
import { useToast } from '../../../lib/toast.js';
import { ConnectImapDialog } from './ConnectImapDialog.js';
import { pulseConnectUrl, useDisconnectPulse, useForceSyncPulse, usePulseActivityLogQuery, useReactivatePulse, useUpdatePulseSettings } from './api.js';

export const STATUS_LABEL: Record<PulseConnectionResponse['status'], { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-success-soft text-success' },
  needs_reconnect: { label: 'Needs reconnect', className: 'bg-amber-soft text-amber-text' },
  disabled: { label: 'Disabled', className: 'bg-surface-subtle text-text-muted' },
};

export const PROVIDER_LABEL: Record<PulseConnectionResponse['provider'], string> = {
  imap: 'IMAP + App Password',
  google: 'Google Workspace (Gmail API)',
  microsoft: 'Microsoft 365 (Graph API)',
};

const OUTCOME_LABEL: Record<PulseActivityLogEntry['outcome'], { label: string; className: string }> = {
  outreach_recorded: { label: 'Outreach recorded', className: 'text-text-secondary' },
  lead_created: { label: 'Lead created', className: 'font-semibold text-success' },
  activity_logged: { label: 'Reply logged', className: 'font-semibold text-primary' },
  skipped_not_outreach: { label: 'Skipped', className: 'text-text-muted' },
  skipped_excluded: { label: 'Skipped (excluded)', className: 'text-text-muted' },
  skipped_auto_reply: { label: 'Skipped (mailing list)', className: 'text-text-muted' },
  skipped_ooo: { label: 'Skipped (out of office)', className: 'text-text-muted' },
  skipped_bounce: { label: 'Skipped (bounce)', className: 'text-text-muted' },
  skipped_ai_review: { label: 'Needs AI review', className: 'font-semibold text-warning' },
  skipped_ai_rejected: { label: 'Skipped (AI: not a lead)', className: 'text-text-muted' },
  error: { label: 'Error', className: 'font-semibold text-danger' },
};

const RECENT_ACTIVITY_PREVIEW = 5;

/** `collapsible` renders a compact summary (header, last sync, stats) with the
 * activity log, settings and disconnect behind a "Details" toggle — used when
 * the user has several mailboxes so the page doesn't grow with each one. A
 * connection that needs attention starts expanded. */
export function PulseConnectionCard({ connection, collapsible = false }: { connection: PulseConnectionResponse; collapsible?: boolean }) {
  const updateSettings = useUpdatePulseSettings();
  const disconnectMutation = useDisconnectPulse();
  const reactivateMutation = useReactivatePulse();
  const forceSyncMutation = useForceSyncPulse();
  const { data: activityLog } = usePulseActivityLogQuery(connection.id);
  const [backfillDays, setBackfillDays] = useState(String(connection.backfillDays));
  const [excludedDomains, setExcludedDomains] = useState(connection.excludedDomains);
  const [subjectFilter, setSubjectFilter] = useState(connection.subjectKeywordFilter ?? '');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [showReconnectImapDialog, setShowReconnectImapDialog] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const toast = useToast();

  const status = STATUS_LABEL[connection.status];
  const isDisabled = connection.status === 'disabled';
  const needsReconnect = connection.status === 'needs_reconnect';
  const [expanded, setExpanded] = useState(!collapsible || needsReconnect);
  const showDetails = !collapsible || expanded;

  const reactivate = async () => {
    try {
      await reactivateMutation.mutateAsync(connection.id);
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to reconnect.';
      toast.error(detail);
    }
  };

  const forceSync = async () => {
    try {
      const result = await forceSyncMutation.mutateAsync(connection.id);
      if (result.lastSyncError) toast.error(`Sync finished with an error: ${result.lastSyncError}`);
      else toast.success('Synced.');
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to sync.';
      toast.error(detail);
    }
  };

  const saveSettings = async () => {
    await updateSettings.mutateAsync({
      id: connection.id,
      input: {
        backfillDays: Number(backfillDays) || connection.backfillDays,
        excludedDomains,
        subjectKeywordFilter: subjectFilter.trim() || null,
      },
    });
    setSavedAt(Date.now());
  };

  return (
    <div className="flex flex-col gap-4 rounded-dialog border border-border bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[15px] font-semibold">{connection.emailAddress}</span>
          <span className="text-xs text-text-muted">
            Owner: {connection.ownerDisplayName} · {PROVIDER_LABEL[connection.provider]}
          </span>
        </div>
        <span className={`flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-semibold ${status.className}`}>{status.label}</span>
      </div>

      {needsReconnect && (
        <div role="alert" className="flex flex-col gap-2.5 rounded-control bg-amber-soft px-3 py-2 text-sm text-amber-text">
          <span>
            This mailbox needs to be reconnected — {connection.provider === 'imap' ? 'the app password may have been revoked' : 'access may have expired or been revoked'}.
          </span>
          {connection.provider === 'imap' ? (
            <Button size="sm" variant="secondary" className="self-start" onClick={() => setShowReconnectImapDialog(true)}>
              Reconnect
            </Button>
          ) : (
            <a
              href={pulseConnectUrl(connection.provider)}
              className="inline-flex h-8 w-fit items-center justify-center rounded-chip border border-border-strong bg-surface px-3 text-[13px] font-semibold text-ink hover:bg-surface-subtle"
            >
              Reconnect
            </a>
          )}
        </div>
      )}

      {isDisabled && (
        <div role="status" className="rounded-control bg-surface-subtle px-3 py-2 text-sm text-text-secondary">
          Disconnected — not syncing. Its settings are kept, so reconnecting doesn't need the app password or OAuth consent again.
        </div>
      )}

      <div className="flex items-center justify-between gap-4 text-[13px] text-text-secondary">
        <span>Last sync: {connection.lastSyncAt ? new Date(connection.lastSyncAt).toLocaleString('en-US') : 'Never yet'}</span>
        <div className="flex items-center gap-2">
          {connection.status === 'active' && (
            <Button size="sm" variant="secondary" loading={forceSyncMutation.isPending} onClick={forceSync}>
              Sync now
            </Button>
          )}
          {collapsible && (
            <Button size="sm" variant="secondary" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Hide details' : 'Details'}
            </Button>
          )}
        </div>
      </div>
      {connection.lastSyncError && <p className="text-xs text-danger">Last error: {connection.lastSyncError}</p>}

      {activityLog && (
        <div className="flex items-center gap-5 border-t border-divider pt-3.5 text-[13px]">
          <span>
            <span className="font-semibold text-ink">{activityLog.stats.processed}</span> <span className="text-text-muted">messages processed</span>
          </span>
          <span>
            <span className="font-semibold text-success">{activityLog.stats.leadsCreated}</span> <span className="text-text-muted">leads created</span>
          </span>
          {activityLog.stats.errors > 0 && (
            <span>
              <span className="font-semibold text-danger">{activityLog.stats.errors}</span> <span className="text-text-muted">errors</span>
            </span>
          )}
        </div>
      )}

      {showDetails && activityLog && activityLog.recent.length > 0 && (
        <div className="border-t border-divider pt-3.5">
          <h3 className="mb-2 text-[13px] font-semibold text-ink">Recent activity</h3>
          <ul className="m-0 flex flex-col gap-1.5" style={{ listStyle: 'none' }}>
            {(showAllActivity ? activityLog.recent : activityLog.recent.slice(0, RECENT_ACTIVITY_PREVIEW)).map((entry) => (
              <li key={entry.id} className="flex items-center justify-between gap-3 text-xs">
                <span className="flex min-w-0 items-center gap-1.5 text-text-muted">
                  <span className="shrink-0">{entry.direction === 'sent' ? 'Sent' : 'Received'}</span>
                  {entry.skipReason && <span className="truncate text-text-faint">· {entry.skipReason}</span>}
                </span>
                {entry.leadId ? (
                  <Link to="/leads/$leadId" params={{ leadId: entry.leadId }} className={`shrink-0 hover:underline ${OUTCOME_LABEL[entry.outcome].className}`}>
                    {OUTCOME_LABEL[entry.outcome].label}
                  </Link>
                ) : entry.contactId ? (
                  <Link to="/contacts/$contactId" params={{ contactId: entry.contactId }} className={`shrink-0 hover:underline ${OUTCOME_LABEL[entry.outcome].className}`}>
                    {OUTCOME_LABEL[entry.outcome].label}
                  </Link>
                ) : (
                  <span className={`shrink-0 ${OUTCOME_LABEL[entry.outcome].className}`}>{OUTCOME_LABEL[entry.outcome].label}</span>
                )}
              </li>
            ))}
          </ul>
          {activityLog.recent.length > RECENT_ACTIVITY_PREVIEW && (
            <button type="button" onClick={() => setShowAllActivity((v) => !v)} className="mt-2 text-xs font-semibold text-primary hover:underline">
              {showAllActivity ? 'Show less' : `Show all ${activityLog.recent.length}`}
            </button>
          )}
        </div>
      )}

      {showDetails && !isDisabled && (
      <div className="border-t border-divider pt-3.5">
        <h3 className="mb-2.5 text-[13px] font-semibold text-ink">Settings</h3>
        <div className="flex flex-col gap-3">
          <label className="flex max-w-[180px] flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Backfill on connect (days)
            <input
              type="number"
              min={1}
              max={365}
              value={backfillDays}
              onChange={(e) => setBackfillDays(e.target.value)}
              className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
            />
          </label>
          <TagInput
            label="Excluded domains/addresses"
            value={excludedDomains}
            onChange={setExcludedDomains}
            placeholder="e.g. yourcompany.com, teammate@example.com"
          />
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Subject keyword filter (optional)
            <input
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              placeholder="Only track threads whose subject contains this"
              className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
            />
          </label>
          <div className="flex items-center gap-2.5">
            <Button size="sm" variant="secondary" loading={updateSettings.isPending} onClick={saveSettings}>
              Save settings
            </Button>
            {savedAt && Date.now() - savedAt < 4000 && <span className="text-xs font-medium text-success">Saved</span>}
          </div>
        </div>
      </div>
      )}

      {(showDetails || isDisabled) && (
      <div className="border-t border-divider pt-3.5">
        {isDisabled ? (
          <Button size="sm" variant="secondary" loading={reactivateMutation.isPending} onClick={reactivate}>
            Reconnect
          </Button>
        ) : (
          <Button size="sm" variant="secondary" loading={disconnectMutation.isPending} onClick={() => disconnectMutation.mutate(connection.id)}>
            Disconnect
          </Button>
        )}
      </div>
      )}

      {showReconnectImapDialog && <ConnectImapDialog initialEmail={connection.emailAddress} onClose={() => setShowReconnectImapDialog(false)} />}
    </div>
  );
}
